import { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { toast } from "react-toastify";
import styles from "./Institute.module.css";

const allowedExtensions = [".xlsx", ".xls"];

const getApiErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  "Error uploading institute referral codes";

const ReferralCodeUploadSection = ({ token, triggerLabel = "Update Users" }) => {
  const [open, setOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState("");

  const handleClose = () => {
    if (!isUploading) {
      setOpen(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setUploadResult(null);
    setUploadError("");

    if (!file) {
      setSelectedFile(null);
      return;
    }

    const fileName = file.name.toLowerCase();
    const isExcelFile = allowedExtensions.some((extension) =>
      fileName.endsWith(extension)
    );

    if (!isExcelFile) {
      setSelectedFile(null);
      setUploadError("Please upload a .xlsx or .xls file.");
      event.target.value = "";
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async (event) => {
    event.preventDefault();

    if (!selectedFile) {
      setUploadError("Please select an Excel file to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("excelFile", selectedFile);
    setIsUploading(true);
    setUploadError("");
    setUploadResult(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_KEY}/institute/upload-referral-codes`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setUploadResult(response.data);
      toast.success(response.data?.message || "Institute referral upload processed");
    } catch (error) {
      const message = getApiErrorMessage(error);
      setUploadError(message);
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        className={styles.referralUploadTrigger}
        onClick={() => setOpen(true)}
      >
        {triggerLabel}
      </Button>

      <Dialog
        open={open}
        fullWidth
        maxWidth="md"
        onClose={handleClose}
        PaperProps={{
          className: styles.referralDialogPaper,
        }}
      >
        <DialogTitle className={styles.referralDialogTitle}>
          Update Users
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={isUploading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <section className={styles.referralUploadSection}>
            <div className={styles.referralUploadHeader}>
              <div>
                <h3>Upload Institute Referral Codes</h3>
                <p>Upload an Excel file to bulk update users' institute referral codes.</p>
              </div>
              <div className={styles.requiredColumns}>
                <span>Required columns</span>
                <code>user_id</code>
                <code>institute_referral_id</code>
              </div>
            </div>

            <form className={styles.referralUploadForm} onSubmit={handleUpload}>
              <label className={styles.fileInputLabel}>
                <span>{selectedFile ? selectedFile.name : "Choose .xlsx or .xls file"}</span>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  disabled={isUploading}
                />
              </label>
              <Button
                type="submit"
                variant="contained"
                className="filled-button"
                disabled={isUploading || !selectedFile}
              >
                {isUploading ? (
                  <>
                    <CircularProgress size={18} color="inherit" />
                    Uploading
                  </>
                ) : (
                  "Upload"
                )}
              </Button>
            </form>

            {uploadError && <div className={styles.uploadError}>{uploadError}</div>}

            {uploadResult && (
              <div className={styles.uploadResult}>
                <div className={styles.uploadSummary}>
                  <div>
                    <span>Total rows</span>
                    <strong>{uploadResult.totalRows ?? 0}</strong>
                  </div>
                  <div>
                    <span>Updated rows</span>
                    <strong>{uploadResult.updatedRows ?? 0}</strong>
                  </div>
                  <div>
                    <span>Skipped rows</span>
                    <strong>{uploadResult.skippedRows ?? 0}</strong>
                  </div>
                </div>

                {uploadResult.invalidRows?.length > 0 && (
                  <div className={styles.invalidRowsWrapper}>
                    <h4>Invalid rows</h4>
                    <div className={styles.invalidRowsTableScroll}>
                      <table className={styles.invalidRowsTable}>
                        <thead>
                          <tr>
                            <th>Row number</th>
                            <th>User ID</th>
                            <th>Institute referral ID</th>
                            <th>Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {uploadResult.invalidRows.map((row, index) => (
                            <tr key={`${row.rowNumber}-${index}`}>
                              <td>{row.rowNumber}</td>
                              <td>{row.user_id}</td>
                              <td>{row.institute_referral_id}</td>
                              <td className="text-left">{row.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReferralCodeUploadSection;
